import { useClipboard } from '@chakra-ui/react';
import { useEffect } from 'react';
import { MarketplaceInfo } from 'src/api/v1-api';
import { FREE_TIER_POSITION } from 'src/pages/_sites/[site]/admin/tier-options';

export const getListingUrl = (urlBase: string, listingId: number | undefined) => {
  if (urlBase === '') return '';
  const newUrl = new URL(`listing/${listingId}`, urlBase);
  return newUrl.toString();
};

export const getOrderUrl = (urlBase: string, orderId: number | undefined) => {
  if (urlBase === '') return '';
  const newUrl = new URL(`order/${orderId}`, urlBase);
  return newUrl.toString();
};

export const getRoleAccountUrl = (urlBase: string, roleId: number | undefined) => {
  if (urlBase === '') return '';
  const newUrl = new URL(`account/roles/edit/${roleId}`, urlBase);
  return newUrl.toString();
};

export const isProdEnabled = () => {
  return process.env.NEXT_PUBLIC_ENV !== 'production';
};

export const SUPPORTED_AYRSHARE_SOCIAL_NETWORKS = [
  'instagram',
  'youtube',
  'facebook',
  'twitter',
  'tiktok',
];
export const INSTAGRAM_POST_NETWORK_TYPES = [
  {
    label: 'post',
    mediaTypes: ['image', 'video'],
  },
  {
    label: 'stories',
    mediaTypes: ['image', 'video'],
  },
  {
    label: 'reels',
    mediaTypes: ['video'],
  },
];
export const FACEBOOK_POST_NETWORK_TYPES = [
  {
    label: 'post',
    mediaTypes: ['image', 'video'],
  },
  {
    label: 'stories',
    mediaTypes: ['image', 'video'],
  },
  {
    label: 'reels',
    mediaTypes: ['video'],
  },
];
export const YOUTUBE_POST_NETWORK_TYPES = [
  {
    label: 'post',
    mediaTypes: ['video'],
  },
  {
    label: 'shorts',
    mediaTypes: ['video'],
  },
];

export const useCustomClipboard = (text: string) => {
  // useClipboard is stupid in the current version of Chakra UI, we can remove this after we upgrade to 2.8
  const { hasCopied, onCopy, setValue } = useClipboard(text);

  useEffect(() => {
    setValue(text);
  }, [text]);

  return {
    hasCopied,
    onCopy,
  };
};

export const getMarketplaceUrl = (marketplace: MarketplaceInfo) => {
  let defaultDomain;
  const marketplaceDomain = marketplace?.withloma ? 'withloma.com' : 'loma.tech';
  switch (process.env.NEXT_PUBLIC_ENV) {
    case 'development':
      defaultDomain = 'localhost:3030';
      break;
    case 'staging':
      defaultDomain = marketplace?.withloma ? 'staging.withloma.com' : 'staging.loma.tech';
      break;
    case 'production':
      defaultDomain = marketplaceDomain;
      break;
    default:
      defaultDomain = marketplaceDomain;
  }

  let marketplaceUrl = marketplace?.slug + '.' + defaultDomain;
  // Set the marketplace URL to the custom domain included in the marketplace object
  // since apexDomain will get set async and may not be available when the page loads
  if (marketplace?.domain?.domain && marketplace?.domain?.status === 'active') {
    marketplaceUrl = marketplace.domain.domain;
  }

  // Only add https:// if we're not in development
  marketplaceUrl =
    process.env.NEXT_PUBLIC_ENV === 'development'
      ? `http://${marketplaceUrl}`
      : `https://${marketplaceUrl}`;

  return marketplaceUrl;
};

export const socialNetworks = [
  { label: 'Instagram', value: 'instagram' },
  { label: 'YouTube', value: 'youtube' },
  { label: 'Discord', value: 'discord' },
  { label: 'X', value: 'twitter' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'Facebook Group', value: 'facebook-group' },
  { label: 'Pinterest', value: 'pinterest' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'Threads', value: 'threads' },
  { label: 'Other', value: 'other' },
];

export const isSampleListing = (
  marketplace: MarketplaceInfo | null,
  listingSource: string,
): boolean => {
  return (
    marketplace?.tier?.position === FREE_TIER_POSITION &&
    marketplace?.status === 'preview' &&
    listingSource !== 'loma'
  );
};
